// components/MapComponent.tsx - Updated with flyTo functionality
import React, { useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { FaPhone, FaEdit, FaTrash } from 'react-icons/fa';
import { Point } from '../types';
import { userIcon, pointIcon, startIcon, endIcon, doneIcon } from '../utils/mapUtils';

interface MapComponentProps {
  center: [number, number];
  points: Point[];
  route: number[] | null;
  routeGeometry: Array<[number, number]>;
  visitedPoints: Set<number>;
  travelProfile: string;
  onMapClick: (e: L.LeafletMouseEvent) => void;
  onEditPoint: (point: Point) => void;
  onRemovePoint: (id: number) => void;
  onToggleVisited: (id: number) => void;
  flyToLocation?: { lat: number; lng: number } | null; // New prop
}

// Component to handle flying to a location
function FlyToLocation({ location }: { location: { lat: number; lng: number } | null }) {
  const map = useMap();
  
  React.useEffect(() => {
    if (location) {
      map.flyTo([location.lat, location.lng], 16, {
        duration: 1.5,
        easeLinearity: 0.25
      });
    }
  }, [location, map]);
  
  return null;
}

function RecenterMap({ center }: { center: [number, number] }) {
  const map = useMap();
  React.useEffect(() => {
    map.flyTo(center, 15);
  }, [center, map]);
  return null;
}

export const MapComponent: React.FC<MapComponentProps> = ({
  center,
  points,
  route,
  routeGeometry,
  visitedPoints,
  travelProfile,
  onMapClick,
  onEditPoint,
  onRemovePoint,
  onToggleVisited,
  flyToLocation,
}) => {
  const getRouteCoordinates = (): [number, number][] => {
    if (!route || points.length === 0) return [];
    
    if (routeGeometry.length > 0) {
      return routeGeometry;
    }
    
    const allPoints = [
      { lat: center[0], lng: center[1], name: 'My Location' },
      ...points
    ];
    
    return route.map(index => [allPoints[index].lat, allPoints[index].lng]);
  };

  return (
    <MapContainer
      center={center}
      zoom={15}
      style={{ height: '100%', width: '100%' }}
      className="z-0"
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      <RecenterMap center={center} />
      <FlyToLocation location={flyToLocation} />

      {/* User location marker */}
      <Marker position={center} icon={userIcon}>
        <Popup>
          <div className="text-center">
            <p className="font-bold">Your Location</p>
            <p className="text-sm text-gray-600">
              {center[0].toFixed(4)}, {center[1].toFixed(4)}
            </p>
          </div>
        </Popup>
      </Marker>

      {/* Points markers */}
      {points.map((point, index) => {
        let icon = pointIcon;
        const isVisited = visitedPoints.has(point.id);
        
        if (isVisited) {
          icon = doneIcon;
        } else if (route && route.length > 0) {
          const routeIndex = route.indexOf(index + 1);
          if (routeIndex === 1) icon = startIcon;
          else if (routeIndex === route.length - 1) icon = endIcon;
        }
        
        return (
          <Marker
            key={point.id}
            position={[point.lat, point.lng]}
            icon={icon}
          >
            <Popup>
              <div className="text-center">
                <p className="font-bold">{point.name}</p>
                {point.contact && (
                  <p className="text-sm text-gray-600">
                    <FaPhone className="inline mr-1 text-blue-500" />
                    {point.contact}
                  </p>
                )}
                <p className="text-sm text-gray-600">
                  {point.lat.toFixed(4)}, {point.lng.toFixed(4)}
                </p>
                {isVisited && (
                  <p className="text-xs text-green-600 mt-1">
                    ✅ Visited
                  </p>
                )}
                {route && !isVisited && (
                  <p className="text-xs text-blue-600 mt-1">
                    Stop #{route.indexOf(index + 1) + 1}
                  </p>
                )}
                <div className="flex gap-2 mt-2">
                  {!isVisited && (
                    <>
                      <button
                        onClick={() => onEditPoint(point)}
                        className="flex-1 px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                      >
                        <FaEdit className="inline mr-1" /> Edit
                      </button>
                      <button
                        onClick={() => onRemovePoint(point.id)}
                        className="flex-1 px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                      >
                        <FaTrash className="inline mr-1" /> Remove
                      </button>
                    </>
                  )}
                  {isVisited && (
                    <button
                      onClick={() => onToggleVisited(point.id)}
                      className="flex-1 px-3 py-1 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm"
                    >
                      Undo Visit
                    </button>
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}

      {/* Route polyline */}
      {route && route.length > 1 && (
        <Polyline
          positions={getRouteCoordinates()}
          color={travelProfile === 'driving-car' ? '#2563eb' : travelProfile === 'foot-walking' ? '#16a34a' : '#dc2626'}
          weight={4}
          opacity={0.8}
        />
      )}

      {/* Click handler for adding points */}
      <MapClickHandler onMapClick={onMapClick} />
    </MapContainer>
  );
};

// Separate component for map click handler
function MapClickHandler({ onMapClick }: { onMapClick: (e: L.LeafletMouseEvent) => void }) {
  const map = useMap();
  
  React.useEffect(() => {
    map.on('click', onMapClick);
    return () => {
      map.off('click', onMapClick);
    };
  }, [map, onMapClick]);
  
  return null;
}